/**
 * Pure helpers for Settings → Jira section (keep SettingsJiraSection CRAP ≤ 30).
 */

import type { JiraConnectionAck, JiraConnectionSettings } from '../../services/clients/types';
import {
  configuredLabel,
  jiraAckChipLabel,
  jiraAckChipOk,
  jiraConnectionBanner,
  type ConnectionAckBanner,
} from './settingsHelpers';
import {
  jiraOauthReturnMessage,
  parseJiraOauthReturnParam,
} from './settingsDeepLink';

export function initialJiraOauthReturnHint(
  search: string | undefined = typeof window !== 'undefined' ? window.location.search : undefined,
): string | null {
  if (!search) return null;
  return jiraOauthReturnMessage(
    parseJiraOauthReturnParam(new URLSearchParams(search).get('jira')),
  );
}

export function stripJiraReturnQueryFromUrl(href: string): string | null {
  const url = new URL(href);
  if (!url.searchParams.has('jira')) return null;
  url.searchParams.delete('jira');
  return `${url.pathname}${url.search}${url.hash}`;
}

export function jiraStatusFallbackLabel(
  oauthConnected: boolean,
  oauthSiteName: string,
  apiTokenConnected: boolean,
  lastFour: string | null,
): string {
  if (oauthConnected) return `OAuth · ${oauthSiteName || 'Atlassian'}`;
  return configuredLabel(apiTokenConnected, lastFour);
}

export function jiraApiTokenConnected(
  tokenConfigured: boolean,
  jira: Pick<JiraConnectionSettings, 'baseUrl' | 'email'>,
): boolean {
  return tokenConfigured && Boolean(jira.baseUrl.trim()) && Boolean(jira.email.trim());
}

export interface JiraConnectionChrome {
  connected: boolean;
  chipOk: boolean;
  chipLabel: string;
  banner: ConnectionAckBanner | null;
}

export function resolveJiraConnectionChrome(input: {
  ack: JiraConnectionAck | null;
  oauthConnected: boolean;
  oauthSiteName: string;
  apiTokenConnected: boolean;
  lastFour: string | null;
}): JiraConnectionChrome {
  const connected = input.oauthConnected || input.apiTokenConnected;
  const chipLabel = input.ack
    ? jiraAckChipLabel(input.ack, connected)
    : jiraStatusFallbackLabel(
        input.oauthConnected,
        input.oauthSiteName,
        input.apiTokenConnected,
        input.lastFour,
      );
  return {
    connected,
    chipOk: jiraAckChipOk(input.ack, connected),
    chipLabel,
    banner: jiraConnectionBanner(input.ack, connected),
  };
}

export function jiraOauthReturnHintColor(hint: string): string {
  return hint.includes('No se pudo') ? 'var(--z-danger)' : 'var(--z-ok, #166534)';
}
